import { useEffect, useState } from "react";

// Android Chrome (and Firefox/Edge on Android) support the real Fullscreen API,
// unlike iPhone Safari — so here we can go truly immersive. The Fullscreen API
// only works inside a user gesture, so we enter on the first interaction: a
// pointerdown covers both a tap AND the start of a scroll/drag. A deliberate
// exit disarms it, so the user is never yanked back in.

export const isAndroid = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent);
};

type Options = {
  enabled: boolean;
};

type Result = {
  isAndroid: boolean;
  active: boolean;
};

export function useAndroidFullscreen({ enabled }: Options): Result {
  const [android] = useState(isAndroid);
  const active = android && enabled;

  useEffect(() => {
    if (!active) return;
    if (!document.documentElement.requestFullscreen) return;

    let armed = true;

    // First action (tap or the pointerdown that begins a scroll/drag) → fullscreen.
    // Target the document root so the WHOLE page (header, drawer, every component
    // and body-level portals) goes fullscreen — not just the stream/video area.
    const tryEnter = () => {
      if (!armed || document.fullscreenElement) return;
      // Must run synchronously in the gesture to keep the user activation.
      document.documentElement
        .requestFullscreen()
        // .then(() => (screen.orientation as any)?.lock?.("landscape")) // TODO: orientation lock (only valid while fullscreen, Android-only)
        .catch(() => {
          /* rejected (no activation / not allowed) — the next gesture retries */
        });
    };

    // A deliberate exit (system back-gesture) must not be fought on the next tap.
    const onFsChange = () => {
      if (!document.fullscreenElement) armed = false;
    };

    window.addEventListener("pointerdown", tryEnter);
    document.addEventListener("fullscreenchange", onFsChange);

    return () => {
      window.removeEventListener("pointerdown", tryEnter);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, [active]);

  return { isAndroid: android, active };
}
